// =============================================================================
// Response unwrapping for admin inventory endpoints. APICore resolves to the axios
// response, whose body is `{ status, data, message }`; list endpoints put either a
// bare array or `{ data: rows, pagination }` inside `data`. These replace the
// `(data as any)?.data?.data?.data || (data as any)?.data?.data || …` chains.
// =============================================================================

export interface ListPagination {
  current_page?: number;
  per_page?: number;
  total?: number;
  total_pages?: number;
}

// Strip the axios layer and the `{ status, data, message }` envelope. A null `data`
// (e.g. "no active seat map") stays null rather than falling back to the envelope.
const body = (res: any) => {
  const envelope = res?.data;
  if (envelope && typeof envelope === "object" && !Array.isArray(envelope) && "data" in envelope) return envelope.data;
  return envelope ?? res;
};

/** A single-record response → the record (or undefined while loading). */
export const unwrapItem = <T = any>(res: any): T | undefined => {
  const b = body(res);
  return b == null ? undefined : (b as T);
};

/**
 * A list response → `{ rows, pagination }`, whichever of the two shapes came back.
 * `rowsKey` names the array when an endpoint doesn't use `data` (events use `events`).
 */
export const unwrapList = <T = any>(res: any, rowsKey = "data"): { rows: T[]; pagination?: ListPagination } => {
  const b = body(res);
  if (Array.isArray(b)) return { rows: b };
  if (b && Array.isArray(b[rowsKey])) return { rows: b[rowsKey], pagination: b.pagination };
  return { rows: [] };
};
